import { defineStore } from 'pinia'

import ManagementResource from '@/api/resources/management'
import SensorResource from '@/api/resources/sensor'
import MaintenanceResource from '@/api/resources/maintenance'
import {
  consolidatedMetricMeta,
  type IAlarmTarget,
  type IAreaIndicators,
  type IConsolidatedReport,
  type IDailyRuntime,
  type IForecastGrid,
  type IForecastSavePayload,
  type IForecastVsActual,
  type IManagementArea,
  type IManagementModule,
  type IMonthConsolidation,
  type ISensorBreakdown,
} from '@/interfaces/entities/management'
import type { ISensor } from '@/interfaces/entities/sensor'
import { translateAlarmType } from '@/interfaces/enums'
import { useCompanyStore } from '@/store/company'
import { useErrorStore } from '@/store/error'
import { reportErrorMessage } from '@/services/report-error'
import { formatPeriodDate, getAnalysisPeriodRange, toMonthKey } from '@/utils/date/date-time'

export type ManagementViewMode = 'single' | 'comparison'

// Cada entrada de comparação carrega seu PRÓPRIO período — permite comparar a
// mesma área em meses diferentes, ou áreas diferentes em períodos diferentes.
export interface IComparisonEntry {
  id: string
  areaId: number
  areaName: string
  period: Date[]
  periodQuery: string
  periodLabel: string
}

interface IManagementState {
  module: IManagementModule | null
  roots: IManagementArea[]
  nodesById: Record<number, IManagementArea>
  selectedPath: number[]
  selectedSensorId: number | null
  period: Date[]
  viewMode: ManagementViewMode
  comparisonEntries: IComparisonEntry[]
  indicatorsByKey: Record<string, IAreaIndicators>
  dailyRuntimeByKey: Record<string, IDailyRuntime>
  companySensors: ISensor[]
  consolidatedReports: IConsolidatedReport[]
  consolidatedRows: Record<string, IMonthConsolidation>
  // Visão consolidada: áreas adicionadas (tabelas mensais) e ano analisado.
  consolidatedAreaIds: number[]
  consolidatedYear: number
  forecastYear: number
  forecastGrids: Record<string, IForecastGrid>
  forecastVsActual: Record<string, IForecastVsActual>
  sensorBreakdowns: Record<string, ISensorBreakdown>
  alarmTargets: IAlarmTarget[]
  loadingTree: boolean
  loadingIndicators: boolean
  generatingReport: boolean
  savingForecasts: boolean
  savingAlarmTargets: boolean
}

export function forecastGridKey(areaId: number, year: number): string {
  return `${areaId}:${year}`
}

export function forecastVsActualKey(areaId: number, sensorId: number | null, year: number): string {
  return `${areaId}:${sensorId ?? 'all'}:${year}`
}

export function metricsSignature(metrics: string[]): string {
  return [...metrics].sort().join(',')
}

export function consolidatedKey(
  areaId: number,
  sensorId: number | null,
  metrics: string[],
  monthKey: string,
): string {
  return `${areaId}:${sensorId ?? 'all'}:${metricsSignature(metrics)}:${monthKey}`
}

function indexTree(roots: IManagementArea[]): Record<number, IManagementArea> {
  const map: Record<number, IManagementArea> = {}
  const walk = (nodes: IManagementArea[]) => {
    nodes.forEach((node) => {
      map[node.id] = node
      walk(node.children || [])
    })
  }
  walk(roots)
  return map
}

function defaultPeriod(days: number): Date[] {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - (days || 30))
  return [start, end]
}

export const useManagementStore = defineStore({
  id: 'management',
  persist: {
    // Mantém os dados do período analisado mesmo após refresh / troca de aba.
    paths: [
      'viewMode',
      'period',
      'selectedPath',
      'selectedSensorId',
      'comparisonEntries',
      'indicatorsByKey',
      'consolidatedReports',
      'consolidatedRows',
      'consolidatedAreaIds',
      'consolidatedYear',
      'forecastYear',
    ],
    afterRestore: (ctx) => ctx.store.reviveDates(),
  },

  state: (): IManagementState => ({
    module: null,
    roots: [],
    nodesById: {},
    selectedPath: [],
    selectedSensorId: null,
    period: [],
    viewMode: 'single',
    comparisonEntries: [],
    indicatorsByKey: {},
    dailyRuntimeByKey: {},
    companySensors: [],
    consolidatedReports: [],
    consolidatedRows: {},
    consolidatedAreaIds: [],
    consolidatedYear: new Date().getFullYear(),
    forecastYear: new Date().getFullYear(),
    forecastGrids: {},
    forecastVsActual: {},
    sensorBreakdowns: {},
    alarmTargets: [],
    loadingTree: false,
    loadingIndicators: false,
    generatingReport: false,
    savingForecasts: false,
    savingAlarmTargets: false,
  }),

  getters: {
    /** Áreas "pai" de cada coluna: null (raiz) seguido das áreas do caminho selecionado. */
    columnParents(state): (IManagementArea | null)[] {
      const parents: (IManagementArea | null)[] = [null]
      state.selectedPath.forEach((areaId) => {
        const node = state.nodesById[areaId]
        if (node) parents.push(node)
      })
      return parents
    },

    activeArea(state): IManagementArea | null {
      const id = state.selectedPath[state.selectedPath.length - 1]
      return id ? state.nodesById[id] ?? null : null
    },

    favoriteAreaId(state): number | null {
      return state.module?.favorite_area_id ?? null
    },

    // Exibe o componente de área favorita só quando habilitado nas configurações.
    showFavoriteOverview(state): boolean {
      return state.module?.show_favorite_overview ?? true
    },

    favoriteArea(state): IManagementArea | null {
      const id = state.module?.favorite_area_id
      return id ? state.nodesById[id] ?? null : null
    },

    periodQuery(state): string {
      const period = state.period
      if (!period || period.length < 2 || !(period[0] instanceof Date) || Number.isNaN(period[0].getTime())) return ''
      return formatPeriodDate(period)
    },

    areaIndicators() {
      return (areaId: number): IAreaIndicators | null => {
        const key = `${areaId}|${this.periodQuery}`
        return this.indicatorsByKey[key] ?? null
      }
    },

    // Indicadores de uma área para um período arbitrário (usado na comparação).
    areaIndicatorsByQuery(state) {
      return (areaId: number, periodQuery: string): IAreaIndicators | null =>
        state.indicatorsByKey[`${areaId}|${periodQuery}`] ?? null
    },

    areaDailyRuntime() {
      return (areaId: number): IDailyRuntime | null => {
        const key = `${areaId}|${this.periodQuery}`
        return this.dailyRuntimeByKey[key] ?? null
      }
    },

    canCompare(state): boolean {
      return state.comparisonEntries.length >= 1
    },
  },

  actions: {
    async init() {
      await Promise.all([this.fetchModule(), this.fetchTree(), this.fetchCompanySensors()])
      if (this.period.length < 2) {
        this.period = defaultPeriod(this.module?.default_window_days ?? 30)
      }
      // Ao abrir, já calcula os indicadores da área favorita (mostrados no topo).
      if (this.favoriteAreaId) this.fetchIndicators(this.favoriteAreaId)
    },

    async fetchModule() {
      try {
        this.module = await ManagementResource.getModule()
      } catch {
        this.module = null
      }
    },

    async fetchTree() {
      this.loadingTree = true
      try {
        const data = await ManagementResource.getAreas()
        this.roots = Array.isArray(data) ? data : (data?.data ?? [])
        this.nodesById = indexTree(this.roots)
        // Remove do caminho áreas que já não existem
        this.selectedPath = this.selectedPath.filter((id) => this.nodesById[id])
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar as áreas.'])
      } finally {
        this.loadingTree = false
      }
    },

    async fetchCompanySensors() {
      try {
        this.companySensors = await SensorResource.getAllSensors()
      } catch {
        this.companySensors = []
      }
    },

    selectArea(columnIndex: number, areaId: number) {
      this.selectedPath = this.selectedPath.slice(0, columnIndex).concat(areaId)
      this.selectedSensorId = null
      this.fetchIndicators(areaId)
    },

    selectSensor(columnIndex: number, sensorId: number) {
      // mantém o caminho até a coluna do sensor; a área pai é columnParents[columnIndex]
      this.selectedPath = this.selectedPath.slice(0, columnIndex)
      this.selectedSensorId = sensorId
      const parent = this.columnParents[columnIndex]
      if (parent) this.fetchIndicators(parent.id)
    },

    setPeriod(period: Date[]) {
      this.period = period
      if (this.favoriteAreaId) this.fetchIndicators(this.favoriteAreaId)
      if (this.activeArea) this.fetchIndicators(this.activeArea.id)
    },

    // Atualiza configurações gerais do módulo (ex.: exibir área favorita).
    async updateModuleSettings(params: { show_favorite_overview?: boolean; title?: string; enabled?: boolean }) {
      try {
        this.module = await ManagementResource.updateModule(params)
        return true
      } catch {
        useErrorStore().setErrors(['Não foi possível salvar as configurações do módulo.'])
        return false
      }
    },

    async setFavoriteArea(areaId: number | null) {
      try {
        this.module = await ManagementResource.updateModule({ favorite_area_id: areaId })
        if (areaId) this.fetchIndicators(areaId)
      } catch {
        useErrorStore().setErrors(['Não foi possível definir a área favorita.'])
      }
    },

    setViewMode(mode: ManagementViewMode) {
      this.viewMode = mode
    },

    // Adiciona uma comparação capturando o período atual (snapshot). Permite
    // comparar a mesma área em períodos diferentes.
    addComparisonEntry(areaId: number, period: Date[]) {
      if (period.length < 2) return
      const periodQuery = formatPeriodDate(period)
      const periodLabel = `${this.formatPeriod(period[0])} – ${this.formatPeriod(period[1])}`
      const exists = this.comparisonEntries.some((e) => e.areaId === areaId && e.periodQuery === periodQuery)
      if (exists) return

      this.comparisonEntries = [
        ...this.comparisonEntries,
        {
          id: `${areaId}-${periodQuery}`,
          areaId,
          areaName: this.nodesById[areaId]?.name ?? 'Área',
          period: [...period],
          periodQuery,
          periodLabel,
        },
      ]
      this.fetchIndicators(areaId, { period })
    },

    removeComparisonEntry(id: string) {
      this.comparisonEntries = this.comparisonEntries.filter((entry) => entry.id !== id)
    },

    formatPeriod(date: Date): string {
      return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    },

    async fetchIndicators(areaId: number, opts: { period?: Date[]; refresh?: boolean } = {}) {
      const period = opts.period ?? this.period
      const refresh = opts.refresh ?? false
      if (period.length < 2) return
      const periodQuery = formatPeriodDate(period)
      const key = `${areaId}|${periodQuery}`
      // Cache de sessão: não refaz a chamada se já temos o resultado (a menos que refresh).
      if (!refresh && this.indicatorsByKey[key]) return
      this.loadingIndicators = true
      try {
        const { startDate, endDate } = getAnalysisPeriodRange(period)
        const result = await ManagementResource.getAreaIndicators(areaId, {
          start_date: startDate,
          end_date: endDate,
          ...(refresh ? { refresh: true } : {}),
        })
        this.indicatorsByKey = { ...this.indicatorsByKey, [key]: result }
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar os indicadores da área.'])
      } finally {
        this.loadingIndicators = false
      }
    },

    // Série diária de tempo rodando x parado da área. Cache de sessão por área|período.
    async fetchDailyRuntime(areaId: number, opts: { period?: Date[]; refresh?: boolean } = {}) {
      const period = opts.period ?? this.period
      const refresh = opts.refresh ?? false
      if (period.length < 2) return
      const periodQuery = formatPeriodDate(period)
      const key = `${areaId}|${periodQuery}`
      if (!refresh && this.dailyRuntimeByKey[key]) return
      try {
        const { startDate, endDate } = getAnalysisPeriodRange(period)
        const result = (await ManagementResource.getDailyRuntime(areaId, {
          start_date: startDate,
          end_date: endDate,
          ...(refresh ? { refresh: true } : {}),
        })) as IDailyRuntime
        this.dailyRuntimeByKey = { ...this.dailyRuntimeByKey, [key]: result }
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar o gráfico diário de paradas.'])
      }
    },

    // Força recálculo (ignora cache de sessão e do backend) da favorita, área ativa e comparadas.
    refreshIndicators() {
      if (this.favoriteAreaId) this.fetchIndicators(this.favoriteAreaId, { refresh: true })
      if (this.activeArea) {
        this.fetchIndicators(this.activeArea.id, { refresh: true })
        this.fetchDailyRuntime(this.activeArea.id, { refresh: true })
      }
      this.comparisonEntries.forEach((entry) => this.fetchIndicators(entry.areaId, { period: entry.period, refresh: true }))
    },

    // Limpa o cache de indicadores e recalcula (ex.: após criar/editar tarifa, que muda o custo).
    clearIndicators() {
      this.indicatorsByKey = {}
      if (this.favoriteAreaId) this.fetchIndicators(this.favoriteAreaId)
      if (this.activeArea) this.fetchIndicators(this.activeArea.id)
    },

    async createArea(parentId: number | null, name: string) {
      await ManagementResource.createArea({ name, parent_id: parentId })
      await this.fetchTree()
    },

    async renameArea(areaId: number, name: string) {
      await ManagementResource.updateArea(areaId, { name })
      await this.fetchTree()
    },

    async deleteArea(areaId: number) {
      await ManagementResource.deleteArea(areaId)
      this.selectedPath = this.selectedPath.filter((id) => id !== areaId)
      this.comparisonEntries = this.comparisonEntries.filter((entry) => entry.areaId !== areaId)
      await this.fetchTree()
    },

    async attachSensor(areaId: number, sensorId: number, indicators: string[]) {
      await ManagementResource.attachSensor(areaId, { sensor_id: sensorId, indicators })
      await this.fetchTree()
      this.invalidateArea(areaId)
    },

    // Aplica de uma vez as mudanças de sensores da área: adiciona os novos e
    // remove os desmarcados (1 só refetch da árvore).
    async syncAreaSensors(
      areaId: number,
      changes: { add: { sensorId: number; indicators: string[] }[]; remove: number[] },
    ) {
      const operations = [
        ...changes.add.map((item) =>
          ManagementResource.attachSensor(areaId, { sensor_id: item.sensorId, indicators: item.indicators }),
        ),
        ...changes.remove.map((areaSensorId) => ManagementResource.detachSensor(areaId, areaSensorId)),
      ]
      const results = await Promise.allSettled(operations)
      await this.fetchTree()
      this.invalidateArea(areaId)
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed) useErrorStore().setErrors([`${failed} operação(ões) não puderam ser concluídas.`])
    },

    async updateSensorIndicators(areaId: number, areaSensorId: number, indicators: string[]) {
      await ManagementResource.updateAreaSensor(areaId, areaSensorId, { indicators })
      await this.fetchTree()
      this.invalidateArea(areaId)
    },

    async detachSensor(areaId: number, areaSensorId: number) {
      await ManagementResource.detachSensor(areaId, areaSensorId)
      await this.fetchTree()
      this.invalidateArea(areaId)
    },

    invalidateArea(areaId: number) {
      const next = { ...this.indicatorsByKey }
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${areaId}|`)) delete next[key]
      })
      this.indicatorsByKey = next
      if (this.activeArea?.id === areaId) this.fetchIndicators(areaId)
      this.comparisonEntries
        .filter((entry) => entry.areaId === areaId)
        .forEach((entry) => this.fetchIndicators(entry.areaId, { period: entry.period }))
    },

    resetSelection() {
      this.selectedPath = []
      this.selectedSensorId = null
    },

    // Monta o payload da consolidação (colunas, linhas e totais) já calculado.
    buildConsolidatedPayload() {
      const now = new Date()
      return this.consolidatedReports.map((report) => {
        const columns = report.metrics.map((key) => {
          const meta = consolidatedMetricMeta(key)
          return { key, label: meta?.label ?? key, unit: meta?.unit ?? '' }
        })
        // Ordem cronológica crescente: mais antigo à esquerda, mais recente à direita.
        // i=0 é o mês mais antigo (monthsBack-1 meses atrás); o último é o atual.
        const rows = Array.from({ length: report.monthsBack }, (_, i) => {
          const monthsAgo = report.monthsBack - 1 - i
          const date = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          const cached = this.consolidatedRows[consolidatedKey(report.areaId, report.sensorId, report.metrics, monthKey)]
          return {
            // Rótulo numérico simplificado: MM/YYYY (ex.: 01/2026).
            month_label: `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
            values: cached?.values ?? {},
          }
        })
        const totals: Record<string, number> = {}
        report.metrics.forEach((key) => {
          const meta = consolidatedMetricMeta(key)
          const vals = rows.map((r) => r.values[key]).filter((v): v is number => typeof v === 'number')
          if (vals.length) {
            const sum = vals.reduce((a, b) => a + b, 0)
            totals[key] = meta?.agg === 'avg' ? sum / vals.length : sum
          }
        })
        return {
          target_label: report.sensorLabel ? `${report.areaLabel} › ${report.sensorLabel}` : report.areaLabel,
          months_back: report.monthsBack,
          columns,
          rows,
          totals,
        }
      })
    },

    setForecastYear(year: number) {
      this.forecastYear = year
    },

    // Grade do previsto (área + sensores) de um ano. Cache por área:ano.
    async fetchForecasts(areaId: number, year: number): Promise<IForecastGrid | null> {
      try {
        const grid = (await ManagementResource.getForecasts(areaId, year)) as IForecastGrid
        this.forecastGrids[forecastGridKey(areaId, year)] = grid
        return grid
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar o previsto.'])
        return null
      }
    },

    forecastGrid(areaId: number, year: number): IForecastGrid | null {
      return this.forecastGrids[forecastGridKey(areaId, year)] ?? null
    },

    // Salva a grade do ano (PUT). Atualiza o cache e invalida o gráfico do par área/ano.
    async saveForecasts(areaId: number, payload: IForecastSavePayload): Promise<boolean> {
      this.savingForecasts = true
      try {
        const grid = (await ManagementResource.saveForecasts(
          areaId,
          payload as unknown as Record<string, unknown>,
        )) as IForecastGrid
        this.forecastGrids[forecastGridKey(areaId, payload.year)] = grid
        Object.keys(this.forecastVsActual)
          .filter((key) => key.startsWith(`${areaId}:`) && key.endsWith(`:${payload.year}`))
          .forEach((key) => delete this.forecastVsActual[key])
        return true
      } catch {
        useErrorStore().setErrors(['Não foi possível salvar o previsto.'])
        return false
      } finally {
        this.savingForecasts = false
      }
    },

    // Série previsto x realizado (consumo total). Cache por área:sensor:ano.
    async fetchForecastVsActual(
      areaId: number,
      opts: { sensorId?: number | null; year?: number; refresh?: boolean } = {},
    ): Promise<IForecastVsActual | null> {
      const year = opts.year ?? this.forecastYear
      const key = forecastVsActualKey(areaId, opts.sensorId ?? null, year)
      if (this.forecastVsActual[key] && !opts.refresh) return this.forecastVsActual[key]
      try {
        const data = (await ManagementResource.getForecastVsActual(areaId, year, {
          sensorId: opts.sensorId ?? null,
          refresh: opts.refresh,
        })) as IForecastVsActual
        this.forecastVsActual[key] = data
        return data
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar previsto x realizado.'])
        return null
      }
    },

    // Desdobramento por sensor (previsto x realizado) de um mês. Cache por área:mês.
    async fetchSensorBreakdown(
      areaId: number,
      month: string,
      opts: { refresh?: boolean } = {},
    ): Promise<ISensorBreakdown | null> {
      const key = `${areaId}:${month}`
      if (this.sensorBreakdowns[key] && !opts.refresh) return this.sensorBreakdowns[key]
      try {
        const data = (await ManagementResource.getSensorBreakdown(areaId, month, opts)) as ISensorBreakdown
        this.sensorBreakdowns[key] = data
        return data
      } catch {
        useErrorStore().setErrors(['Não foi possível carregar o detalhamento por sensor.'])
        return null
      }
    },

    // Gera o PDF do relatório gerencial enviando os dados JÁ calculados (a API não reprocessa).
    async generateReport() {
      this.generatingReport = true
      try {
        const favInd = this.favoriteAreaId ? this.areaIndicators(this.favoriteAreaId) : null
        const favorite =
          this.favoriteArea && favInd
            ? {
                name: this.favoriteArea.name,
                totals: favInd.totals,
                alarms: (favInd.alarms || []).map((a) => ({
                  label: translateAlarmType(a.alarm_type),
                  count: a.count,
                  open_count: a.open_count,
                  avg_duration_minutes: a.avg_duration_minutes,
                })),
                alarm_classifications: favInd.alarm_classifications || [],
              }
            : null

        const comparison = this.comparisonEntries
          .map((entry) => ({
            area_name: entry.areaName,
            period_label: entry.periodLabel,
            totals: this.areaIndicatorsByQuery(entry.areaId, entry.periodQuery)?.totals ?? {},
          }))
          .filter((c) => Object.keys(c.totals).length > 0)

        const periodLabel =
          this.period.length === 2 ? `${this.formatPeriod(this.period[0])} – ${this.formatPeriod(this.period[1])}` : '—'

        // Mês de referência = mês final do período analisado (ou o mês atual).
        const referenceMonth = this.period.length === 2 ? toMonthKey(this.period[1]) : toMonthKey(new Date())

        // Previsto x realizado (gráfico, ano vigente) e desdobramento por sensor
        // (tabela, mês de referência) são montados AQUI (não no backend durante a
        // renderização) para o PDF não bloquear com consultas ao InfluxDB.
        const forecastVsActual = this.favoriteAreaId
          ? await this.fetchForecastVsActual(this.favoriteAreaId, { year: new Date().getFullYear() })
          : null
        const sensorBreakdown = this.favoriteAreaId
          ? await this.fetchSensorBreakdown(this.favoriteAreaId, referenceMonth)
          : null

        // Indicadores operacionais do "Resumo Gerencial": porta aberta, temperatura
        // e máquina vêm enriquecidos do backend (favInd); meta×realizado de alarmes
        // e ranking de classificações são montados aqui.
        const operational = favInd
          ? {
              tempo_total_porta_aberta: favInd.tempo_total_porta_aberta ?? null,
              door_open_ranking: favInd.door_open_ranking ?? [],
              temperatures: favInd.temperatures ?? [],
              machine_runtime: favInd.machine_runtime ?? [],
              alarm_targets: this.buildAlarmTargetsComparison(favInd.alarms ?? []),
              classification_ranking: favInd.alarm_classifications ?? [],
            }
          : null

        const maintenance = await this.fetchMaintenanceSummary()

        const payload = {
          company_name: useCompanyStore().getCurrentCompany?.name ?? 'Empresa',
          period_label: periodLabel,
          favorite,
          comparison,
          consolidated: this.buildConsolidatedPayload(),
          forecast_vs_actual: forecastVsActual,
          sensor_breakdown: sensorBreakdown,
          operational,
          maintenance,
          // Para o indicador de eficiência energética (consumo do mês vs. mesmo
          // mês do ano anterior), calculado no backend a partir do histórico de faturas.
          company_id: useCompanyStore().getCurrentCompanyId,
          reference_month: referenceMonth,
        }

        const response = await ManagementResource.generateReport(payload)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const disposition = (response.headers?.['content-disposition'] as string | undefined) ?? ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const filename = match?.[1] || 'relatorio-gerencial.pdf'

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        useErrorStore().setErrors([reportErrorMessage(error, 'Não foi possível gerar o relatório.')])
      } finally {
        this.generatingReport = false
      }
    },

    // Meta × realizado por tipo de alarme: realizado = contagem do período (favInd).
    buildAlarmTargetsComparison(alarms: { alarm_type: string; count: number }[]) {
      const realized: Record<string, number> = Object.fromEntries(alarms.map((a) => [a.alarm_type, a.count]))
      return this.alarmTargets.map((target) => ({
        alarm_type: target.alarm_type,
        label: translateAlarmType(target.alarm_type),
        meta: target.target_count,
        realizado: realized[target.alarm_type] ?? 0,
      }))
    },

    // Resumo de manutenções do período (reusa o endpoint de relatório de manutenções).
    async fetchMaintenanceSummary() {
      if (this.period.length !== 2) return null
      try {
        const result = await MaintenanceResource.getMaintenanceReport(
          this.period[0].toISOString(),
          this.period[1].toISOString(),
        )
        return result ?? null
      } catch {
        return null
      }
    },

    async fetchAlarmTargets() {
      try {
        const result = await ManagementResource.listAlarmTargets()
        this.alarmTargets = (Array.isArray(result) ? result : result?.data ?? []) as IAlarmTarget[]
      } catch {
        this.alarmTargets = []
      }
    },

    async saveAlarmTargets(targets: { alarm_type: string; target_count: number }[]) {
      this.savingAlarmTargets = true
      try {
        const result = await ManagementResource.upsertAlarmTargets(targets)
        this.alarmTargets = (Array.isArray(result) ? result : result?.data ?? []) as IAlarmTarget[]
        return true
      } catch {
        useErrorStore().setErrors(['Não foi possível salvar as metas de alarme.'])
        return false
      } finally {
        this.savingAlarmTargets = false
      }
    },

    // Converte datas restauradas do localStorage (strings) de volta para Date.
    reviveDates() {
      if (Array.isArray(this.period)) {
        this.period = this.period.map((date) => new Date(date as unknown as string))
      }
      this.comparisonEntries = (this.comparisonEntries || []).map((entry) => ({
        ...entry,
        period: (entry.period || []).map((date) => new Date(date as unknown as string)),
      }))
    },

    // --- Visão consolidada (tabelas mensais por área, comparáveis) ---
    addConsolidatedArea(areaId: number) {
      if (this.consolidatedAreaIds.includes(areaId)) return
      this.consolidatedAreaIds = [...this.consolidatedAreaIds, areaId]
    },

    removeConsolidatedArea(areaId: number) {
      this.consolidatedAreaIds = this.consolidatedAreaIds.filter((id) => id !== areaId)
    },

    setConsolidatedYear(year: number) {
      this.consolidatedYear = year
    },

    addConsolidatedReport(report: IConsolidatedReport) {
      this.consolidatedReports = [...this.consolidatedReports, report]
    },

    removeConsolidatedReport(id: string) {
      this.consolidatedReports = this.consolidatedReports.filter((r) => r.id !== id)
    },

    // Busca a consolidação de um mês para um relatório (cache de sessão persistido).
    async fetchReportMonth(
      report: IConsolidatedReport,
      monthKey: string,
      refresh = false,
    ): Promise<IMonthConsolidation | null> {
      const key = consolidatedKey(report.areaId, report.sensorId, report.metrics, monthKey)
      if (!refresh && this.consolidatedRows[key]) return this.consolidatedRows[key]
      try {
        const result = await ManagementResource.getMonthlyConsolidation(report.areaId, monthKey, {
          sensorId: report.sensorId,
          metrics: report.metrics,
          refresh,
        })
        this.consolidatedRows = { ...this.consolidatedRows, [key]: result }
        return result
      } catch {
        return null
      }
    },
  },
})
